import type {ReactElement} from 'react';
import {Button, IconPlacement, ButtonSize, ButtonType} from '@Components/button';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {IconWithTooltip} from '@Components/icon-with-tooltip/icon-with-tooltip';
import {useEventWizardIsPaidTicketingFeatureAvailable, useEventWizardIsTicketingCountrySupported, useEventWizardTicketingConfig} from '@Components/event-wizard/event-wizard.hooks';
import {onEventWizardTicketingDonationChange} from '@Components/event-wizard/event-wizard-slice';
import {requireTicketingSelection} from '@Components/event-wizard/event-wizard-ticketing/ticketing-gating.helper';
import {openTicketingDonationModal} from '@Components/modals/ticketing-donation-modal/ticketing-donation-modal.library';
import {formatExactNumberInUSD} from '@Utils/number.util';
import {useAppDispatch} from '@/hooks';
import styles from './ticketing-donation-row.module.scss';

/**
 * The "Collect donations" row under the registration form, for RSVP and Tickets. Unset it offers "Set up"; once
 * set it summarises the title and minimum with Edit / Remove. All input lives in the donation modal, the sole
 * writer of `ticketingConfig.donation` apart from Remove. The caller hides it on a recurring event.
 */
export function TicketingDonationRow(): ReactElement {
  const dispatch = useAppDispatch();
  const ticketingConfig = useEventWizardTicketingConfig();
  const {donation} = ticketingConfig;
  const isPaidTicketingFeatureAvailable = useEventWizardIsPaidTicketingFeatureAvailable();
  const isTicketingCountrySupported = useEventWizardIsTicketingCountrySupported();

  // Donations take money, so setting one up passes the same Premium + US + flag gate as paid tickets.
  const onSetUp = (): void => {
    if (!requireTicketingSelection(isPaidTicketingFeatureAvailable, isTicketingCountrySupported)) {
      return;
    }
    openTicketingDonationModal();
  };

  const onRemove = (): void => {
    dispatch(onEventWizardTicketingDonationChange({...donation, enabled: false}));
  };

  /**
   * Where the fees on a donation land: the organiser's pass-fees setting, which the toggle under this row edits.
   * Before a donation is set up, a free / RSVP event has no ticket revenue to absorb fees from, so enabling one
   * flips that setting to the buyer (onEventWizardTicketingDonationChange) and the unset row says so up front.
   */
  const getFeesTooltipText = (): string => {
    const willEnablingPassFees = !donation.enabled && (ticketingConfig.isRsvp || ticketingConfig.price <= 0);
    const areFeesPassedToBuyer = willEnablingPassFees || ticketingConfig.passFeesToBuyer;
    return areFeesPassedToBuyer ? window.i18next.t('pmwjs_event_ticketing_donation_row_fees_passed') : window.i18next.t('pmwjs_event_ticketing_donation_row_fees_absorbed');
  };

  const getTitleWithFeesTooltip = (title: string): ReactElement => {
    return (
      <div className="gap-2 flex-row-align-center">
        <Text className="ct-neutral-primary" val={title} size={TextSize.LG} bold />
        <IconWithTooltip icon="icon-info" size={IconSize.SIZE_ICON_16} className="ct-neutral-tertiary flex-shrink-0" tooltipText={getFeesTooltipText()} tooltipClassName={styles.feesTooltip} ariaLabel={title} />
      </div>
    );
  };

  const getDonationIcon = (isSet = false): ReactElement => {
    return (
      <div className={`${styles.donationIcon} ${isSet ? styles.isSet : ''}`}>
        <Icon icon="icon-causes" size={IconSize.SIZE_ICON_20} />
      </div>
    );
  };

  const getUnsetRow = (): ReactElement => {
    return (
      <div className="gap-4 flex-row-align-center flex-justify-between _full-width">
        <div className="flex-1 gap-3 flex-row-align-center">
          {getDonationIcon()}
          <div className="flex-column-align-start">
            {getTitleWithFeesTooltip(window.i18next.t('pmwjs_event_ticketing_collect_donations'))}
            <Text className="ct-neutral-secondary" val={window.i18next.t('pmwjs_event_ticketing_collect_donations_sub')} size={TextSize.SM} />
          </div>
        </div>
        <Button type={ButtonType.SECONDARY} size={ButtonSize.SMALL} text={window.i18next.t('pmwjs_event_ticketing_donation_set_up')} onClick={onSetUp} />
      </div>
    );
  };

  const getSetRow = (): ReactElement => {
    return (
      <div className="gap-4 flex-row-align-center flex-justify-between _full-width">
        <div className="flex-1 gap-3 flex-row-align-center">
          {getDonationIcon(true)}
          <div className="flex-column-align-start">
            {getTitleWithFeesTooltip(window.i18next.t('pmwjs_event_ticketing_donation_default_title'))}
            <Text
              className="ct-neutral-secondary"
              val={window.i18next.t('pmwjs_event_ticketing_donation_summary', {amount: formatExactNumberInUSD(donation.minAmount)})}
              size={TextSize.SM}
            />
          </div>
        </div>
        <div className="gap-1 flex-row-align-center flex-shrink-0">
          <Button type={ButtonType.TERTIARY} size={ButtonSize.SMALL} icon="icon-pencil" iconPlacement={IconPlacement.LEFT} onClick={openTicketingDonationModal} />
          <Button type={ButtonType.TERTIARY} size={ButtonSize.SMALL} icon="icon-close" iconPlacement={IconPlacement.LEFT} onClick={onRemove} />
        </div>
      </div>
    );
  };

  const getDonationRow = (): ReactElement => {
    if (donation.enabled) {
      return getSetRow();
    }
    return getUnsetRow();
  };

  return getDonationRow();
}
